import { format } from 'date-fns';
import { formatCurrency } from '@/utils/format';
import { ChatAnalysis, WhatsAppMessage } from '@/types/whatsapp';
import { 
  Document, 
  Paragraph, 
  Table, 
  TableRow, 
  TableCell, 
  HeadingLevel, 
  BorderStyle,
  TextRun,
  Packer,
  AlignmentType
} from 'docx';

// Utility: Generate Participant List
function generateParticipantsList(participants: ChatAnalysis['participantStats']): Paragraph[] {
  return participants.map(p => new Paragraph({
    bullet: { level: 0 },
    children: [new TextRun(`${p.name}: ${p.messageCount} messages`)]
  }));
}

// Utility: Generate Key Topics List
function generateTopicsList(topics: ChatAnalysis['keyTopics']): Paragraph[] {
  return topics.map(topic => new Paragraph({
    bullet: { level: 0 },
    children: [new TextRun(topic)]
  }));
}

// Utility: Generate Delay Analysis
function generateDelayTypesList(delays: Record<string, number>): Paragraph[] {
  return Object.entries(delays).map(([type, count]) => new Paragraph({
    bullet: { level: 0 },
    children: [new TextRun(`${type}: ${count} delays`)]
  }));
}

// Utility: Generate Work Type Breakdown
function generateWorkTypesList(workTypes: Record<string, number>): Paragraph[] {
  return Object.entries(workTypes).map(([type, count]) => new Paragraph({
    bullet: { level: 0 },
    children: [new TextRun(`${type}: ${count} items`)]
  }));
}

// Utility: Generate Activity Timeline
function generateActivityTimeline(messagesByDate: ChatAnalysis['messagesByDate']): Paragraph[] {
  return messagesByDate.map(day => new Paragraph({
    bullet: { level: 0 },
    children: [new TextRun(`${format(new Date(day.date), 'dd MMM yyyy')}: ${day.count} messages`)]
  }));
}

// Utility: Generate Conversation Table
function generateConversationTable(messages: WhatsAppMessage[]): Table {
  return new Table({
    width: {
      size: 100,
      type: 'pct',
    },
    borders: {
      top: { style: BorderStyle.SINGLE, size: 1 },
      bottom: { style: BorderStyle.SINGLE, size: 1 },
      left: { style: BorderStyle.SINGLE, size: 1 },
      right: { style: BorderStyle.SINGLE, size: 1 },
    },
    rows: [
      // Header row
      new TableRow({
        children: [
          new TableCell({ children: [new Paragraph('Date/Time')] }),
          new TableCell({ children: [new Paragraph('Sender')] }),
          new TableCell({ children: [new Paragraph('Message')] }),
        ],
      }),
      // Message rows
      ...messages.map(msg => new TableRow({
        children: [
          new TableCell({ 
            children: [new Paragraph(msg.dateTime || format(msg.timestamp, 'dd MMM yyyy HH:mm'))]
          }),
          new TableCell({ 
            children: [new Paragraph({ children: [new TextRun({ text: msg.sender, bold: true })] })]
          }),
          new TableCell({ 
            children: [new Paragraph(msg.content)]
          }),
        ],
      })),
    ],
  });
}

// Main Function: Generate Document Content
function generateDocument(analysis: ChatAnalysis): Document {
  const timestamp = format(new Date(), 'MMMM do, yyyy HH:mm');

  return new Document({
    sections: [{
      properties: {},
      children: [
        // Title
        new Paragraph({
          text: "Project WhatsApp Chat Analysis",
          heading: HeadingLevel.HEADING_1,
          spacing: { after: 200 }
        }),
        new Paragraph({
          text: `Generated on: ${timestamp}`,
          spacing: { after: 400 }
        }),

        // Summary Section
        new Paragraph({
          text: "Summary",
          heading: HeadingLevel.HEADING_2,
          spacing: { before: 400, after: 200 }
        }),
        new Paragraph({
          children: [
            new TextRun({
              text: `Overview Statistics: Total Messages: ${analysis.totalMessages}, Total Participants: ${analysis.participantStats.length}`,
            }),
          ],
        }),
        new Paragraph({
          text: "Participant Engagement:",
          spacing: { before: 200 }
        }),
        ...generateParticipantsList(analysis.participantStats),
        new Paragraph({
          text: "Key Topics:",
          spacing: { before: 200 }
        }),
        ...generateTopicsList(analysis.keyTopics),

        // Delay Analysis Section
        new Paragraph({
          text: "Delay Analysis",
          heading: HeadingLevel.HEADING_2,
          spacing: { before: 400, after: 200 }
        }),
        new Paragraph(`Total Delays: ${analysis.delayAnalysis.totalDelays}`),
        new Paragraph(`Average Delay Duration: ${analysis.delayAnalysis.averageDelayDuration.toFixed(1)} days`),
        new Paragraph({
          text: "Delay Types:",
          spacing: { before: 200 }
        }),
        ...generateDelayTypesList(analysis.delayAnalysis.delaysByType),

        // Additional Work Analysis Section
        new Paragraph({
          text: "Additional Work Analysis",
          heading: HeadingLevel.HEADING_2,
          spacing: { before: 400, after: 200 }
        }),
        new Paragraph(`Total Items: ${analysis.additionalWorkAnalysis.totalItems}`),
        new Paragraph(`Total Value: ${formatCurrency(analysis.additionalWorkAnalysis.totalValue)}`),
        new Paragraph({
          text: "Work Type Breakdown:",
          spacing: { before: 200 }
        }),
        ...generateWorkTypesList(analysis.additionalWorkAnalysis.typeBreakdown),

        // Activity Timeline Section
        new Paragraph({
          text: "Activity Timeline",
          heading: HeadingLevel.HEADING_2,
          spacing: { before: 400, after: 200 }
        }),
        ...generateActivityTimeline(analysis.messagesByDate),

        // Detailed Conversation Log Section
        new Paragraph({
          text: "Detailed Conversation Log",
          heading: HeadingLevel.HEADING_2,
          spacing: { before: 400, after: 200 }
        }),
        generateConversationTable(analysis.messages),

        // Footer
        new Paragraph({
          text: "Generated by QS Tools WhatsApp Analyzer",
          alignment: AlignmentType.CENTER,
          spacing: { before: 400 }
        }),
        new Paragraph({
          text: `Copyright © ${new Date().getFullYear()}`,
          alignment: AlignmentType.CENTER
        }),
      ],
    }],
  });
}

// Create and Download Transcript
export async function createTranscript(analysis: ChatAnalysis): Promise<void> {
  try {
    // Validate required data
    if (!analysis) {
      throw new Error('Analysis data is required to create a transcript.');
    }

    if (!analysis.messages || !Array.isArray(analysis.messages)) {
      throw new Error('No message data available for transcript generation.');
    }

    if (!analysis.participantStats || !Array.isArray(analysis.participantStats)) {
      throw new Error('Participant statistics are missing.');
    }

    // Generate document
    const doc = generateDocument(analysis);

    // Create blob
    const blob = await Packer.toBlob(doc);

    // Create download link
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `chat_transcript_${format(new Date(), 'yyyy-MM-dd_HH-mm')}.docx`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  } catch (error) {
    const errorMessage = error instanceof Error 
      ? error.message 
      : 'An unexpected error occurred while creating the transcript';
    console.error('Failed to create transcript:', errorMessage);
    throw new Error(errorMessage);
  }
}